import { FeatureGates } from '@services/statsig/constants';

/** Every feature link and the guide drawer render nothing while this gate is closed. */
export const GUIDE_LINKS_GATE = FeatureGates.FEATURE_GUIDE_LINKS;

/**
 * Surface ids identify where a feature link lives for analytics only. Add a
 * constant here when you add a link; there is no registry to update.
 */
export const GUIDE_SURFACES = {
  /** Used by tests and stories only. */
  EXAMPLE: 'example',
  // Slice B (GDN-3685). One id per surface; the entitlement ids name the shared
  // component kind, and the capability travels in the tracking payload.
  PATIENT_CHART_MEDICATIONS: 'patient_chart_medications',
  ENTITLEMENT_OVERLAY: 'entitlement_overlay',
  ENTITLEMENT_DISABLED_CONTROL: 'entitlement_disabled_control',
  ENTITLEMENT_UPGRADE_DIALOG: 'entitlement_upgrade_dialog',
  JOTAUDIT_RULES_CARD: 'jotaudit_rules_card',
  JOTRX_CALLOUT_CLINICIANS: 'jotrx_callout_clinicians',
  JOTRX_CALLOUT_ACCOUNT_SETTINGS: 'jotrx_callout_account_settings',
  JOTRX_CALLOUT_PRACTICE_SETTINGS: 'jotrx_callout_practice_settings',
  JOTRX_BANNER: 'jotrx_banner',
  COVERAGE_WORKSPACE: 'coverage_workspace',
  ELIGIBILITY_ALERTS: 'eligibility_alerts',
  // Slice C (GDN-3686): empty states, practice settings cards, page sentences.
  DOCUMENTS_EMPTY_STATE: 'documents_empty_state',
  LABS_EMPTY_STATE: 'labs_empty_state',
  FORMS_EMPTY_STATE: 'forms_empty_state',
  PRACTICE_SETTINGS_CARD_ORGANIZATION_INFO: 'practice_settings_card_organization_info',
  PRACTICE_SETTINGS_CARD_PREFERENCES: 'practice_settings_card_preferences',
  PRACTICE_SETTINGS_CARD_PERMISSIONS: 'practice_settings_card_permissions',
  PRACTICE_SETTINGS_CARD_REPORT_ACCESS: 'practice_settings_card_report_access',
  PRACTICE_SETTINGS_CARD_PATIENT_REQUIREMENTS: 'practice_settings_card_patient_requirements',
  PRACTICE_SETTINGS_CARD_TEMPLATES: 'practice_settings_card_templates',
  PAGE_MEMBERS: 'page_members',
  PAGE_FORMS: 'page_forms',
  MEMBERS_ROLES_REFERENCE: 'members_roles_reference',
  PATIENT_CHART_ALLERGIES: 'patient_chart_allergies',
  PATIENT_CHART_DIAGNOSES: 'patient_chart_diagnoses',
  PATIENT_CHART_ASSESSMENTS: 'patient_chart_assessments',
  PATIENT_CHART_PROFILE_CONTACTS: 'patient_chart_profile_contacts',
  PATIENT_CHART_ENCOUNTERS: 'patient_chart_encounters',
  PATIENT_CHART_DOCUMENTS: 'patient_chart_documents',
  PATIENT_CHART_FORMS: 'patient_chart_forms',
  PATIENT_CHART_INSURANCE: 'patient_chart_insurance',
  PATIENT_CHART_BILLING: 'patient_chart_billing',
  PATIENT_CHART_MEDICATION_LIST: 'patient_chart_medication_list',
  PATIENT_TREATMENT_PLAN: 'patient_treatment_plan',
  PATIENT_REQUIREMENTS: 'patient_requirements',
  PAYMENT_ANNOUNCEMENT: 'payment_announcement',

  PAGE_SCHEDULE: 'page_schedule',
  PAGE_PATIENTS: 'page_patients',
  PAGE_PRACTICE_SETTINGS: 'page_practice_settings',
  PAGE_ELIGIBILITY_CHECKS: 'page_eligibility_checks',
  PAGE_INSURANCE: 'page_insurance',
  PAGE_COLLECTIONS: 'page_collections',
  PAGE_PRESCRIBING_SETTINGS: 'page_prescribing_settings',
} as const;

export type GuideSurfaceId = (typeof GUIDE_SURFACES)[keyof typeof GUIDE_SURFACES];

/**
 * The holdout experiment (GDN-3707, AC-18). A placeholder name until Alfred
 * confirms it on GDN-3692; it lives here and nowhere else. Its one boolean
 * parameter is false for control and true for treatment; a user outside the
 * experiment gets neither, and follows the gate alone.
 */
export const GUIDE_LINKS_HOLDOUT_EXPERIMENT = 'feature_guide_links_holdout';
export const GUIDE_LINKS_HOLDOUT_PARAM = 'links_enabled';

/**
 * The surfaces the holdout measures: every Slice A and B id. Evaluating a link
 * on one of them logs the exposure, whether the arm renders or suppresses it.
 * Slice C surfaces follow the arm but never log an exposure.
 */
export const HOLDOUT_SURFACES: ReadonlySet<GuideSurfaceId> = new Set<GuideSurfaceId>([
  GUIDE_SURFACES.EXAMPLE,
  GUIDE_SURFACES.PATIENT_CHART_MEDICATIONS,
  GUIDE_SURFACES.ENTITLEMENT_OVERLAY,
  GUIDE_SURFACES.ENTITLEMENT_DISABLED_CONTROL,
  GUIDE_SURFACES.ENTITLEMENT_UPGRADE_DIALOG,
  GUIDE_SURFACES.JOTAUDIT_RULES_CARD,
  GUIDE_SURFACES.JOTRX_CALLOUT_CLINICIANS,
  GUIDE_SURFACES.JOTRX_CALLOUT_ACCOUNT_SETTINGS,
  GUIDE_SURFACES.JOTRX_CALLOUT_PRACTICE_SETTINGS,
  GUIDE_SURFACES.JOTRX_BANNER,
  GUIDE_SURFACES.COVERAGE_WORKSPACE,
  GUIDE_SURFACES.ELIGIBILITY_ALERTS,
]);

/** Query parameter the drawer appends to a product guide URL so the reader can attribute the entry. */
export const GUIDE_LINK_REFERRER_PARAM = 'ref';
export const GUIDE_DRAWER_REFERRER = 'drawer';
